import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequireModule from '@/core/modules/RequireModule';
import RequirePermission from '@/auth/RequirePermission';
import { Modules } from '@/core/modules/moduleKeys';
import { Perm } from '@/auth/permissions';

const HospitalHomePage = lazy(() => import('./pages/HospitalHomePage'));
const HospitalFacilitiesPage = lazy(() => import('./pages/HospitalFacilitiesPage'));
const HospitalStaffPage = lazy(() => import('./pages/HospitalStaffPage'));
const HospitalConfigurationPage = lazy(() => import('./pages/HospitalConfigurationPage'));
const HospitalDevicesPage = lazy(() => import('./pages/HospitalDevicesPage'));
const HospitalDeviceAgentPage = lazy(() => import('./pages/HospitalDeviceAgentPage'));
const HospitalPatientsPage = lazy(() => import('./pages/HospitalPatientsPage'));
const HospitalPatientPage = lazy(() => import('./pages/HospitalPatientPage'));
const HospitalOfflinePage = lazy(() => import('./pages/HospitalOfflinePage'));
const HospitalConsultPage = lazy(() => import('./pages/HospitalConsultPage'));
const HospitalWorklistPage = lazy(() => import('./pages/HospitalWorklistPage'));
const HospitalReferralsPage = lazy(() => import('./pages/HospitalReferralsPage'));
const HospitalPrivacyPage = lazy(() => import('./pages/HospitalPrivacyPage'));
const HospitalPharmacyPage = lazy(() => import('./pages/HospitalPharmacyPage'));
const HospitalBedBoardPage = lazy(() => import('./pages/HospitalBedBoardPage'));
const HospitalAdmissionPage = lazy(() => import('./pages/HospitalAdmissionPage'));
const HospitalLabPage = lazy(() => import('./pages/HospitalLabPage'));
const HospitalMaternityPage = lazy(() => import('./pages/HospitalMaternityPage'));
const HospitalTheatrePage = lazy(() => import('./pages/HospitalTheatrePage'));
const HospitalImagingPage = lazy(() => import('./pages/HospitalImagingPage'));
const HospitalBloodBankPage = lazy(() => import('./pages/HospitalBloodBankPage'));
const HospitalMortuaryPage = lazy(() => import('./pages/HospitalMortuaryPage'));
const HospitalSupplyPage = lazy(() => import('./pages/HospitalSupplyPage'));
const HospitalDispatchPage = lazy(() => import('./pages/HospitalDispatchPage'));
const HospitalTrackingPage = lazy(() => import('./pages/HospitalTrackingPage'));
const HospitalReceiptPage = lazy(() => import('./pages/HospitalReceiptPage'));
const HospitalCashierPage = lazy(() => import('./pages/HospitalCashierPage'));
const HospitalBillingPage = lazy(() => import('./pages/HospitalBillingPage'));
const HospitalClaimsPage = lazy(() => import('./pages/HospitalClaimsPage'));
const HospitalQualityPage = lazy(() => import('./pages/HospitalQualityPage'));
const HospitalReportsPage = lazy(() => import('./pages/HospitalReportsPage'));
const HospitalFeedbackKioskPage = lazy(() => import('./pages/HospitalFeedbackKioskPage'));
const HospitalQueuePage = lazy(() => import('./pages/HospitalQueuePage'));
const HospitalDisplayPage = lazy(() => import('./pages/HospitalQueuePage').then((m) => ({ default: m.HospitalDisplayPage })));
const HospitalTriagePage = lazy(() => import('./pages/HospitalTriagePage'));
const HospitalAppointmentsPage = lazy(() => import('./pages/HospitalAppointmentsPage'));

const gate = (anyOf: string[], page: React.ReactNode) => (
  <RequireModule module={Modules.Health}>
    <RequirePermission anyOf={anyOf}>{page}</RequirePermission>
  </RequireModule>
);

/** Annova Health (docs §278 on). */
export const hospitalRoutes: RouteObject[] = [
  { path: 'hospital', element: gate([Perm.Health.Use], <HospitalHomePage />) },
  { path: 'hospital/patients', element: gate([Perm.Health.Use], <HospitalPatientsPage />) },
  { path: 'hospital/patients/:id', element: gate([Perm.Health.Use], <HospitalPatientPage />) },
  { path: 'hospital/queue', element: gate([Perm.Health.Use], <HospitalQueuePage />) },
  { path: 'hospital/queue/display', element: gate([Perm.Health.Use], <HospitalDisplayPage />) },
  { path: 'hospital/triage/:id', element: gate([Perm.Health.Use], <HospitalTriagePage />) },
  { path: 'hospital/appointments', element: gate([Perm.Health.Use], <HospitalAppointmentsPage />) },
  { path: 'hospital/consult/:id', element: gate([Perm.Health.Use], <HospitalConsultPage />) },
  { path: 'hospital/worklist', element: gate([Perm.Health.Use], <HospitalWorklistPage />) },
  { path: 'hospital/referrals', element: gate([Perm.Health.Use], <HospitalReferralsPage />) },
  { path: 'hospital/privacy', element: gate([Perm.Health.Use], <HospitalPrivacyPage />) },
  { path: 'hospital/pharmacy', element: gate([Perm.Health.Use], <HospitalPharmacyPage />) },
  { path: 'hospital/beds', element: gate([Perm.Health.Use], <HospitalBedBoardPage />) },
  { path: 'hospital/admissions/:id', element: gate([Perm.Health.Use], <HospitalAdmissionPage />) },
  { path: 'hospital/laboratory', element: gate([Perm.Health.Use], <HospitalLabPage />) },
  { path: 'hospital/maternity', element: gate([Perm.Health.Use], <HospitalMaternityPage />) },
  { path: 'hospital/theatre', element: gate([Perm.Health.Use], <HospitalTheatrePage />) },
  { path: 'hospital/imaging', element: gate([Perm.Health.Use], <HospitalImagingPage />) },
  { path: 'hospital/blood-bank', element: gate([Perm.Health.Use], <HospitalBloodBankPage />) },
  { path: 'hospital/mortuary', element: gate([Perm.Health.Use], <HospitalMortuaryPage />) },
  { path: 'hospital/supply', element: gate([Perm.Health.Use], <HospitalSupplyPage />) },
  { path: 'hospital/dispatch', element: gate([Perm.Health.Use], <HospitalDispatchPage />) },
  { path: 'hospital/tracking', element: gate([Perm.Health.Use], <HospitalTrackingPage />) },
  { path: 'hospital/receipt', element: gate([Perm.Health.Use], <HospitalReceiptPage />) },
  { path: 'hospital/cashier', element: gate([Perm.Health.Use], <HospitalCashierPage />) },
  { path: 'hospital/billing', element: gate([Perm.Health.Use], <HospitalBillingPage />) },
  { path: 'hospital/claims', element: gate([Perm.Health.Use], <HospitalClaimsPage />) },
  { path: 'hospital/quality', element: gate([Perm.Health.Use], <HospitalQualityPage />) },
  { path: 'hospital/reports', element: gate([Perm.Health.Use], <HospitalReportsPage />) },
  { path: 'hospital/feedback-kiosk', element: gate([Perm.Health.Use], <HospitalFeedbackKioskPage />) },
  { path: 'hospital/offline', element: gate([Perm.Health.Use], <HospitalOfflinePage />) },
  { path: 'hospital/facilities', element: gate([Perm.Health.Administer], <HospitalFacilitiesPage />) },
  { path: 'hospital/staff', element: gate([Perm.Health.Administer], <HospitalStaffPage />) },
  { path: 'hospital/configuration', element: gate([Perm.Health.Administer], <HospitalConfigurationPage />) },
  { path: 'hospital/devices', element: gate([Perm.Health.Administer], <HospitalDevicesPage />) },
  { path: 'hospital/device-agent', element: gate([Perm.Health.Use], <HospitalDeviceAgentPage />) },
];
